/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import isNil from 'lodash.isnil';
import uniq from 'lodash.uniq';
import {Moment} from 'moment';
import {reInitializeState} from '@ion/applications/src/helpers';
import {AccountHashMapKeyType} from '@ion/core/src/entities/Accounts';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import Device from '@ion/core/src/services/Device';
import {EventCode} from '@ion/core/src/services/EventCodes';
import {
  GlobalSearchResult,
  GlobalSearchResults
} from '@ion/core/src/services/GlobalSearch';
import {
  Asset,
  AugmentedSmarterTrailerSensorData, CrashboxxRecord,
  CVSAssetAggregations,
  CVSAssetType,
  Device as DeviceEntity,
  FindNearbyGeofence,
  FindNearbyType,
  GenericObject,
  GeoFenceStatus,
  HVSRecord,
  LatLong,
  SCDeviceType,
  SensorTypes,
  TagReportType,
  ToolAssetType
} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {ServiceError} from '@ion/core/src/types/services';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import {endOfDay, startOfDay} from '@ion/core/src/utils/DateUtil';
import {removeDuplicate} from '@ion/core/src/utils/ObjectUtil';
import {contains} from '@ion/core/src/utils/StringUtil';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {
  DEFAULT_DURATION,
  DEFAULT_HISTORY_PRESET
} from '@ion/dls/src/maps/components/timeMode/current/constants';
import {TRAFFIC_SUB_LAYER_LIVE_TRAFFIC} from '@ion/dls/src/maps/constants';
import {SavedPlacesProps} from '@ion/dls/src/maps/geofences/SearchAddress';
import {getDefaultSavedMapView} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {HISTORY_LAYER} from '@ion/dls/src/maps/layers/TripHistoryLayerSettings';
import {getDefaultBasemap} from '@ion/dls/src/maps/MapPreferences';
import {
  BaseMapEnum,
  BoundaryMode,
  ContextMenu,
  DateSelectorValues,
  HistoryInputEvents,
  HistoryReportTypeEnum,
  LabelContentType,
  MapLayerSettings,
  MapLayerTypeEnum,
  MapviewInitialExtent,
  MapViews,
  SideSheetView,
  TimeModes,
  ToolbarEnum,
  TrackingFilters,
  TrackingMode
} from '@ion/dls/src/types/types';
import getMonitor from '@ion/telemetry/src/apm/monitors/PagePerformanceMonitor';
import {Route} from '../../routes/RouteDefinitions';
import Features from '../../security/Features';
import {APPLICATION_INITIALIZATION_COMPLETE} from '../../store/actions/initialization';
import {PageState} from '../../types/pages';
import {AsyncState} from '../../types/redux';
import {SET_BOUNDARY_PANEL_OPEN} from '../actions/reports';
import {
  ADD_DEVICE_WITH_SMARTER_TRAILER,
  APPLY_DATE,
  APPLY_GEOFENCE_TO_FILTER,
  CANCEL_LIVE_HISTORY,
  CLEAR_HISTORY_SEARCH_ASSET,
  CLEAR_LIVE_HISTORY_ASSET_SEARCH_LIST,
  CLEAR_SELECTED_FILTERS,
  EXIT_FIND_NEARBY,
  FIND_NEARBY_ERROR,
  FIND_NEARBY_REQUEST,
  FIND_NEARBY_RESPONSE,
  GET_ASSET_DETAILS_ERROR,
  GET_ASSET_DETAILS_REQUEST,
  GET_ASSET_DETAILS_RESPONSE,
  GET_ASSET_TOOLS_ERROR,
  GET_ASSET_TOOLS_REQUEST,
  GET_ASSET_TOOLS_RESPONSE,
  GET_ASSETS_HISTORY_ERROR,
  GET_ASSETS_HISTORY_REQUEST,
  GET_ASSETS_HISTORY_RESPONSE,
  GET_ASSOCIATED_ASSETS_ERROR,
  GET_ASSOCIATED_ASSETS_REQUEST,
  GET_ASSOCIATED_ASSETS_RESPONSE,
  GET_LARGE_FLEET_ERROR,
  GET_LARGE_FLEET_GRID_ERROR,
  GET_LARGE_FLEET_GRID_REQUEST,
  GET_LARGE_FLEET_GRID_RESPONSE,
  GET_LARGE_FLEET_REQUEST,
  GET_LARGE_FLEET_RESPONSE,
  INITIALIZE_ASSET_DETAIL_HISTORY,
  INITIALIZE_OPERATIONAL_LAYERS,
  INITIALIZE_TRACKING,
  INITIALIZE_TRACKING_ASSETS,
  LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_ERROR,
  LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_REQUEST,
  LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_RESPONSE,
  LOAD_ASSET_DETAIL_HISTORY_ERROR,
  LOAD_ASSET_DETAIL_HISTORY_REQUEST,
  LOAD_ASSET_DETAIL_HISTORY_RESPONSE,
  LOAD_SC_DEVICES_ERROR,
  LOAD_SC_DEVICES_REQUEST,
  LOAD_SC_DEVICES_RESPONSE,
  LOAD_SC_DEVICES_UPDATES,
  LOAD_SMARTER_TRAILER_ERROR,
  LOAD_SMARTER_TRAILER_REQUEST,
  LOAD_SMARTER_TRAILER_RESPONSE,
  MAP_INITIALIZED,
  RE_INITIALIZE_TRACKING,
  REFRESH_LARGE_FLEET_STATE,
  RESET_ASSETS_HISTORY,
  RESET_ASSETS_TRACKING,
  RESET_FETCH_LARGE_FLEET_ASSETS,
  RESET_GEOFENCE_STATUS,
  RESET_GRID_FORCE_UPDATE,
  RESET_INITIALIZE_TRACKING,
  RESET_LARGE_FLEET_STATE,
  RESET_SC_DEVICES,
  RESET_TRACKING,
  RUN_LIVE_HISTORY,
  SAVE_GEOFENCE,
  SAVE_GEOFENCE_ERROR,
  SAVE_GEOFENCE_SUCCESS,
  SAVING_GEOFENCE,
  SET_ALL_FILTERS_LOADING_STATE,
  SET_ASSET_CONTENT,
  SET_ASSET_DETAIL_VIEW,
  SET_ASSET_DETAILS_LOADING,
  SET_ASSET_GRID_UPDATE,
  SET_ASSET_HISTORY_GRID_COLUMNS,
  SET_ASSET_SELECTION_TYPE,
  SET_ASSET_STATUS_FILTER_ERROR,
  SET_ASSET_STATUS_FILTER_REQUEST,
  SET_ASSET_STATUS_FILTER_RESPONSE,
  SET_ASSET_TYPE_FILTER_ERROR,
  SET_ASSET_TYPE_FILTER_REQUEST,
  SET_ASSET_TYPE_FILTER_RESPONSE,
  SET_ASSETS_COUNT,
  SET_ASSETS_FILTER_ERROR,
  SET_ASSETS_FILTER_REQUEST,
  SET_ASSETS_FILTER_RESPONSE,
  SET_ASSETS_HISTORY_ERROR,
  SET_ASSETS_HISTORY_INPUT_EVENTS,
  SET_ASSETS_HISTORY_LIVE_INTERVAL,
  SET_ASSETS_HISTORY_REPORT_TYPE,
  SET_ASSETS_HISTORY_UPDATES_LIVE,
  SET_ASSETS_SORT_DESCENDING,
  SET_ASSIGNED_ASSETS_PANEL_OPEN,
  SET_AUTO_REFRESH_STATE,
  SET_BOTTOM_GRID_HEIGHT,
  SET_BOUNDARY_FILTER,
  SET_BOUNDARY_FILTER_MODE,
  SET_CATEGORY_FILTER_ERROR,
  SET_CATEGORY_FILTER_REQUEST,
  SET_CATEGORY_FILTER_RESPONSE,
  SET_CONTEXT_MENU_LOCATION,
  SET_CONTEXT_MENU_VISIBILITY,
  SET_DEVICE_LABEL_CONTENT,
  SET_DEVICES_FILTER_ERROR,
  SET_DEVICES_FILTER_REQUEST,
  SET_DEVICES_FILTER_RESPONSE,
  SET_DISCONNECTED_TOOL_ERROR,
  SET_DISCONNECTED_TOOL_TAG_REPORT,
  SET_EDITABLE_DEVICE,
  SET_FILTER_NAME,
  SET_FIND_BUTTON_STATE,
  SET_FIND_NEARBY_ACTIVE_STATUS,
  SET_FIND_NEARBY_ASSET,
  SET_FIND_NEARBY_DISTANCE,
  SET_FIND_NEARBY_FROM_ITEM,
  SET_FIND_NEARBY_GEOFENCE,
  SET_FIND_NEARBY_LABEL,
  SET_FIND_NEARBY_PANEL_ACTIVE,
  SET_FIND_NEARBY_POPUP_STATE,
  SET_FIND_NEARBY_REFRESH,
  SET_FIND_NEARBY_RESULTS,
  SET_FIND_NEARBY_SELECTED_ASSET,
  SET_FIND_NEARBY_TOGGLE,
  SET_FLEET_LAYER_PERSISTENCE_ACTIVE,
  SET_FLEET_UPDATE_FETCHING,
  SET_FLEET_UPDATE_FLAG,
  SET_FLEET_UPDATE_RESET_FLAG,
  SET_FM_ASSET_INITIALIZED,
  SET_GEOFENCE_BORDER_ONLY,
  SET_GEOFENCE_FILTER_ERROR,
  SET_GEOFENCE_FILTER_OPEN,
  SET_GEOFENCE_FILTER_REQUEST,
  SET_GEOFENCE_FILTER_RESPONSE,
  SET_GEOFENCE_LABEL_STATE,
  SET_GEOFENCE_LAYER_OPACITY,
  SET_GEOFENCE_LOADED,
  SET_GEOFENCE_PANEL_OPEN,
  SET_GRID_COLUMNS,
  SET_GRID_FILTERED_ASSET_IDS,
  SET_GROUP_STATUS,
  SET_GROUPS_FILTER_ERROR,
  SET_GROUPS_FILTER_REQUEST,
  SET_GROUPS_FILTER_RESPONSE,
  SET_HISTORY_ACTIVE,
  SET_HISTORY_FROM,
  SET_HISTORY_PRESET,
  SET_HISTORY_REFRESH,
  SET_HISTORY_RINGS,
  SET_HISTORY_SELECTED_ASSET,
  SET_HISTORY_TO,
  SET_HOVERED_ASSET,
  SET_HOVERED_ASSET_DETAIL_HISTORY_EVENT,
  SET_INITIAL_TRACKING_MAP_EXTENT,
  SET_IS_LAYER_LIST_UPDATE,
  SET_LARGE_FLEET_ASSETS_AGGREGATION_LEVELS,
  SET_LARGE_FLEET_GRID_COUNT,
  SET_LARGE_FLEET_MODE,
  SET_LARGE_FLEET_STATE,
  SET_LAST_SELECTED_ASSET,
  SET_LIVE_HISTORY_ASSET_FETCHING,
  SET_LIVE_HISTORY_ASSET_SEARCH_ACTIVE,
  SET_LIVE_HISTORY_ASSET_SEARCH_LIST,
  SET_LIVE_HISTORY_ASSET_UI_FETCHING,
  SET_LIVE_HISTORY_ERROR,
  SET_LIVE_HISTORY_HIDE_NON_SELECTED_ASSETS,
  SET_LIVE_HISTORY_INITIAL_ASSETS,
  SET_LIVE_HISTORY_INPUT_EVENTS,
  SET_LIVE_HISTORY_IO_PROFILE_FETCHING,
  SET_LIVE_HISTORY_REPORT_TYPE,
  SET_LIVE_HISTORY_SEARCH_TIME,
  SET_LIVE_HISTORY_SELECTED_ASSET,
  SET_LOADER_INDICATOR_STATUS,
  SET_LOCATION_FIND_NEARBY,
  SET_MAP_ONLY_VIEW,
  SET_MAP_VIEWS,
  SET_MAP_WIDGET_SELECTED,
  SET_OPERATIONAL_LAYER_OPACITY,
  SET_OPERATIONAL_LAYER_VISIBILITY,
  SET_OPERATIONAL_LAYERS,
  SET_OPERATIONAL_SUBLAYER_VISIBILITY,
  SET_PANEL_TOP_POSITION,
  SET_PERSIST_LIVE_HISTORY,
  SET_PROCESSED_FILTERS,
  SET_ROUTE_FILTER_ERROR,
  SET_ROUTE_FILTER_REQUEST,
  SET_ROUTE_FILTER_RESPONSE,
  SET_ROUTE_MATCHING_ENABLED,
  SET_SAVE_FILTER_ERROR,
  SET_SAVE_FILTER_REQUEST,
  SET_SC_DEVICES_OPACITY,
  SET_SEARCH_HISTORY,
  SET_SELECTED_ASSET,
  SET_SELECTED_FILTERS_ACTIVE,
  SET_SELECTED_TOOL,
  SET_SELECTED_TRACKING_SEARCH_RESULT,
  SET_SIDESHEET_VIEW,
  SET_STATUS_TO_RESUME_MAP,
  SET_TIME_MODE,
  SET_TIME_MODE_PANEL_OPEN,
  SET_TIMELINE_DURATION,
  SET_TIMELINE_PRESET,
  SET_TOOL_DETAIL_VIEW,
  SET_TOOL_PANEL_OPEN,
  SET_TRACKING_ASSET_COUNT,
  SET_TRACKING_ASSET_COUNT_LOAD,
  SET_TRACKING_ASSET_SNAP_TO_ROAD,
  SET_TRACKING_ASSET_UPDATES_PLAYBACK_STATE,
  SET_TRACKING_BASE_MAP,
  SET_TRACKING_FILTERS,
  SET_TRACKING_GRID_FILTER_MODEL,
  SET_TRACKING_GRID_FILTERED_ASSETS,
  SET_TRACKING_GRID_STATE,
  SET_TRACKING_HISTORY_REPORT,
  SET_TRACKING_MAP_EXTENT,
  SET_TRACKING_MAP_IS_ASSET_DETAIL_HISTORY,
  SET_TRACKING_MAP_LAYERS,
  SET_TRACKING_MAP_POSITION,
  SET_TRACKING_MAP_VIEW,
  SET_TRACKING_MAP_ZOOM,
  SET_TRACKING_MAPVIEW_EXTENT,
  SET_TRACKING_MAPVIEW_INITIAL_EXTENT,
  SET_TRACKING_MODE,
  SET_TRAFFIC_LAYER_OPACITY,
  SET_TRAFFIC_LAYER_SUBLAYERS,
  SET_WEATHER_LAYER_OPACITY,
  SHOW_ASSET_HISTORY_REFRESH_MESSAGE,
  SMARTER_TRAILER_CHECKED,
  SMARTER_TRAILER_SENSOR_NEEDS_UPDATE,
  TOGGLE_ASSET_CLUSTERING,
  TOGGLE_ASSET_HISTORY_GRID_EXPANDED_GROUPS,
  TOGGLE_ASSET_LABELLING,
  TOGGLE_DEVICE_LABELLING,
  TOGGLE_MAP_CONTROLS,
  TOGGLE_MOBILE_FILTERS,
  TRACKING_ASSETS_REQUEST,
  TRACKING_ASSETS_RESPONSE,
  TRACKING_ASSETS_UPDATES,
  TRACKING_SEARCH_ERROR,
  TRACKING_SEARCH_REQUEST,
  TRACKING_SEARCH_RESPONSE,
  UPDATE_ASSET_HISTORY_GRID_COLUMNS,
  UPDATE_GRID_COLUMNS,
  UPDATE_SMARTER_TRAILER_ERROR,
  UPDATE_SMARTER_TRAILER_REQUEST,
  UPDATE_SMARTER_TRAILER_RESPONSE
} from '../actions/tracking';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

// the recent tracking searches kept in the user's preferences - the current
// theme's search shows all of them, legacy only its first
// LEGACY_VISIBLE_SEARCH_HISTORY (see legacySearchHistorySelector) ...
const MAX_SEARCH_HISTORY_ENTRIES = 5;

// defines the map settings state ...
type Maps = {
  mapLayerType: MapLayerTypeEnum[],
  mapLayerSettings: MapLayerSettings[],
  isAssetDetailHistory: boolean,
  initialized: boolean,
  layers?: Array<string>,
  basemap: BaseMapEnum,
  extent?: object | null,
  view?: __esri.View,
  initialExtent?: object | null,
  center?: object | null,
  zoom?: number,
  trafficOpacity: number,
  trafficSubLayers?: string[],
  weatherOpacity: number,
  geofenceOpacity: number,
  coordinates: Object,
  resumeMapStatus?: boolean,
  operationalLayers?: Array<Object>,
  isLayerListUpdate?: boolean,
  islayerVisibilityUpdate?: boolean
  extents?: {
    extent?: MapviewInitialExtent[] | null,
    initialExtent?: MapviewInitialExtent[] | null,
  },
  isFleetLayerPersistenceActive: boolean
};

export type HoveredAsset = {
  asset: Device,
  source: string
}

export type FindNearby = {
  isActive: boolean;
  isLoading: boolean;
  asset: CVSAssetType | null;
  geofence: FindNearbyGeofence | null;
  location: LatLong | null;
  results: Array<Device> | [];
  error: ServiceError | null;
  label: string;
  miles: number;
  type: FindNearbyType | null;
}

export type FindNearbyItem = {
  type: FindNearbyType | null;
  id?: number;
  name: string;
  location: LatLong;
}

export type FindNearbyV2 = {
  isActive: boolean,
  isPanelActive: boolean,
  refresh: boolean,
  distance: number,
  fromItem: FindNearbyItem | null,
  results: Array<Device> | [],
  selectedAsset: any
}

type FindNearbyState = {
  showPopup: boolean;
  toggle: boolean;
}

type UpdateOperationLayer = {
  operationalLayers: {
    title: string;
    layers: {
      id: string
    }[];
  }[],
  prop: string,
  field: string,
  isSublayer?: boolean,
  updated: {
    title: string;
    layerTitle: string;
    sublayerId: string;
  }
}

/**
 * Defines the Tracking Filters State which contains all possible values a user
 * can select and the state of those values which are represented by the items
 * array which contains all available values for the filter, and error object
 * which contains the error related to loading those items (if any) and a loading
 * state which indicates if the items are loading, and a selected property which
 * contains all filters currently selected by the user for that filter
 */

interface TrackingSearch extends AsyncState {
  searchTerm: string | null;
  results: GlobalSearchResults | null;
  selected: GlobalSearchResult | null;
  history: GlobalSearchResult[] | null;
  isShowing: boolean;
  isActive: boolean;
};

export enum AssetSelectionType {
  DETAIL = 'detail',
  HIGHLIGHT = 'highlight'
};

// defines the shape of this reducers state ...
export interface TrackingState extends PageState {
  isInitialized: boolean;
  isFMAssetInitialized: boolean;
  trackingMode: TrackingMode;
  timeMode: TimeModes;
  // the timeline chip left lit, or `null` where Custom is the live choice
  // and the span is the Live History interval instead. Deliberately left out
  // of `RESET_TRACKING`, which the tracking page dispatches as it unmounts -
  // the chip is meant to outlive leaving the page, as the interval it sits
  // beside already does ...
  timelinePreset: number | null;
  // the span the timeline's Custom selector is left holding, kept apart from
  // the span the report runs over. A lit chip speaks for the report, but the
  // selector keeps its own reading underneath it - so lighting 2 Hours over
  // a Custom set to 17 and coming back finds Custom still on 17. Left out of
  // `RESET_TRACKING` for the same reason the chip above it is ...
  timelineDuration: number;
  // History's own preset - unlike `timelinePreset`, Custom is its own
  // `DateSelectorValues.Custom` member rather than `null` - and the range
  // it resolved to. Both read as their own default until the panel is ever
  // applied at least once. Left out of `RESET_TRACKING` for the same
  // reason `timelinePreset`/`timelineDuration` are ...
  historyPreset: DateSelectorValues;
  historyFrom: Date | null;
  historyTo: Date | null;
  // whether a History report has actually been applied, the same
  // `isLiveHistoryActive`/`historyV2.isActive` distinction Timeline's own
  // chip already reads - being in History mode only means the panel is
  // open or was last left on, not that its own Apply was ever pressed ...
  isHistoryActive: boolean;
  // whether the active mode's panel is still on screen. Held apart from the
  // mode itself because selecting the mode the map is already in changes no
  // mode but does ask for the panel back ...
  isTimeModePanelOpen: boolean;
  persistView: {
    bottomGridHeight: number;
  },
  assets: {
    countLoading: boolean;
    filteredAssets: Device[];
    filteredDevices: Device[];
    assets?: Device[];
    updates?: Device[];
    assetsCount?: number;
    hoveredAsset: HoveredAsset | null | undefined;
    selectedAsset: Device | null | undefined;
    lastSelectedAsset: Device | null | undefined;
    panelTopPosition: number;
    assetSelectionType: AssetSelectionType | null;
    selectedAssetTools?: {assetId: string; tools: Asset[]}[] | null;
    smarterTrailer: {
      loading: boolean;
      selectedAssetTrailer: AugmentedSmarterTrailerSensorData;
      devicesHaveTrailerSensors: {
        esn: string;
        apps: SensorTypes[];
      }[];
      devicesChecked: string[];
      sensorNeedsUpdate: boolean;
      sensorUpdateLoading: boolean;
      error: Error;
    }
    assetDetailsLoading: boolean;
    assetDetailsError?: object | null | undefined;
    isAutoRefresh?: boolean;
    clustering: boolean;
    mapClustering: boolean;
    labelling: boolean;
    labelContent: LabelContentType;
    loading: boolean;
    associatedAssets?: {
      assets?: {assetId: string; associatedAssets: Asset[]}[] | null;
      isLoading: boolean;
      error?: object | null | undefined;
    },
    assetsLoading: boolean,
    initialExtent: any,
    isAssetsSortDescending?: boolean;
  };
  history: {
    assets?: HVSRecord[];
    selectedAsset?: HVSRecord | null;
    reportType?: HistoryReportTypeEnum;
    loading?: boolean;
    updatesLive?: boolean;
    isRefresh?: boolean;
    intervalInHours?: number;
    error?: {
      show?: false;
      assetCount?: number;
    };
    inputEvents: HistoryInputEvents[];
    showRefreshMessage: boolean;
    routeMatchingEnabled: boolean;
  };
  historyV2: {
    isActive: boolean;
    isFetching: boolean;
    isUIFetching: boolean;
    displayReport: boolean;
    rings: Array<number> | null;
    isIOProfileFetching: boolean;
    selectedAsset: HVSRecord | null;
    reportType: HistoryReportTypeEnum;
    intervalInHours: number;
    inputEvents: HistoryInputEvents[];
    routeMatchingEnabled: boolean;
    assetSearchList: any[] | null;
    initialAssets: any[];
    assetsSearchActive: boolean;
    hideNonSelectedAssetsActive: boolean;
    error: {
      show: boolean;
      assetCount: number;
    };
    persistLiveHistory: boolean;
  }
  supplyChainDevices: {
    devices: Device[] | null,
    updates: SCDeviceType[] | null,
    opacity: number,
    error: Error,
    deviceToEdit?: DeviceEntity | null,
    isLoaded: boolean,
    isLabelActive: boolean,
    labelContent: LabelContentType
  };
  assetDetailHistoryGrid: {
    history: {
      loading: boolean;
      asset: CVSAssetType | CVSAssetType[] | null;
      hoveredEvent: HVSRecord | null;
      history: any[] | null;
      error: ServiceError | null;
      startDate: Moment | null;
      endDate: Moment | null;
      filterLoading: boolean;
      eventFilter: EventCode[];
      expandedGroups: string[];
      crashEvents: {
        loading: boolean;
        results: {
          results: CrashboxxRecord[];
          totalCount: boolean;
        }
        error: ServiceError | null;
      }
    },
    grid: {
      columnState: ColumnState[];
      preferedColumnState: ColumnState[];
      hasUpdate: boolean;
    }
  };
  tools: {
    disconnectedToolTagReport: null | TagReportType;
    disconnectedToolError: null | Error;
    selectedTool: null | ToolAssetType;
    selectedToolParent?: CVSAssetType | null;
    toolsError?: object | null | undefined;
    toolsLoading: boolean;
    toolPanelOpen: {
      assetId: null | string;
      open: boolean;
    }[];
  };
  assignedAssets: {
    assignedAssetsPanelsOpen: string
  };
  geofences: {
    savedGeofence: null | object;
    savingGeofence: boolean;
    geofenceStatus: null | GeoFenceStatus;
    borderOnly: boolean;
    geofenceLable: boolean;
  }
  fleetMode: {
    assetsAggregation?: CVSAssetAggregations,
    error: ServiceError;
    loading: boolean;
    isLargeFleetMode?: boolean,
    largeFleetExtent?: object,
    refreshMap?: boolean,
    largeFleetAssetCount?: number,
    isLargeFleetAssetsFetched?: boolean,
    largeFleetAssetList: {
      assets?: Device[],
      hasNextPage?: boolean,
      isNextPageLoading?: boolean,
      refresh: boolean;
      updateGrid?: boolean;
    }
  };
  toolDetailView: boolean;
  assetDetailView: boolean;
  isMapSelection: boolean;
  panMapOnly: boolean;
  searchHistory: SavedPlacesProps[],
  geoFencePanelOpen: boolean;
  maps: Maps;
  mobileMapControlsOpen: boolean;
  findNearby: FindNearby;
  findNearbyV2: FindNearbyV2;
  findNearbyState: FindNearbyState;
  search: TrackingSearch;
  filters: TrackingFilters;
  isSelectedFiltersActive: boolean;
  appliedSaveFilter: string;
  processedFilters?: Array<object>;
  selectedFilterName?: string;
  findButtonState: boolean;
  featureFlags: {
    panelDetailFlags: {
      [key: string]: boolean;
    };
    showMapScaleWidget: boolean;
  },
  panelDetailFlags: {
    [key: string]: boolean;
  };
  accountMap: AccountHashMapKeyType | null;
  isAllFiltersLoaded: boolean;
  sideSheetView: SideSheetView
  isMapOnlyView: boolean
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filteredAssetIds: number[] | null,
    filterModel: FilterModel | {},
    state: GenericObject,
    count?: number
  },
  mapViews: MapViews,
  isMobileFilterActive: boolean;
  trackingHistory: {
    routeHistory: {
      selected?: any
    }
  },
  isGeofenceLoaded: boolean;
  v2: {
    fleetUpdateFlag: boolean;
    fleetUpdateResetFlag: boolean;
    fleetUpdateFetchingKeys: string[];
  };
  mapWidgetSelected: ToolbarEnum;
  contextMenu: {
    location: {
      x: number;
      y: number;
      mapPoint: __esri.Point;
    };
    visibility: boolean;
    selected: ContextMenu;
  };
  boundaryMode: BoundaryMode;
  isSnapToRoadEnabled: boolean;
  isAssetUpdatesEnabled: boolean;
}

// defines the initial state for the reducer ...
export const initialState: TrackingState = {
  isInitialized        : false,
  isFMAssetInitialized : false,
  trackingMode         : TrackingMode.Live,
  timeMode             : TimeModes.live,
  timelinePreset       : DEFAULT_DURATION,
  timelineDuration     : DEFAULT_DURATION,
  historyPreset        : DEFAULT_HISTORY_PRESET,
  historyFrom          : null,
  historyTo            : null,
  isHistoryActive      : false,
  isTimeModePanelOpen  : false,
  persistView          : {
    bottomGridHeight : null
  },
  assets : {
    assets              : null,
    updates             : null,
    assetDetailsLoading : false,
    hoveredAsset        : {
      asset  : null,
      source : null
    },
    selectedAsset      : null,
    lastSelectedAsset  : null,
    panelTopPosition   : null,
    assetSelectionType : null,
    smarterTrailer     : {
      loading                   : false,
      selectedAssetTrailer      : null,
      devicesChecked            : [],
      devicesHaveTrailerSensors : [],
      sensorNeedsUpdate         : true,
      sensorUpdateLoading       : false,
      error                     : null
    },
    selectedAssetTools : [],
    isAutoRefresh      : false,
    clustering         : true,
    mapClustering      : null,
    labelling          : true,
    labelContent       : {
      primaryLabel   : 'assetName',
      secondaryLabel : null
    },
    loading          : false,
    associatedAssets : {
      assets    : [],
      isLoading : false,
      error     : null
    },
    assetsLoading          : false,
    assetsCount            : 0,
    initialExtent          : null,
    isAssetsSortDescending : true,
    countLoading           : true,
    filteredAssets         : [],
    filteredDevices        : []
  },
  history : {
    assets          : [],
    selectedAsset   : null,
    reportType      : HistoryReportTypeEnum.Asset,
    loading         : false,
    updatesLive     : false,
    isRefresh       : false,
    intervalInHours : 2,
    error           : {
      show       : false,
      assetCount : 0
    },
    inputEvents          : [],
    showRefreshMessage   : false,
    routeMatchingEnabled : true
  },
  historyV2 : {
    isActive                    : false,
    isFetching                  : false,
    isUIFetching                : false,
    isIOProfileFetching         : false,
    displayReport               : false,
    rings                       : null,
    selectedAsset               : null,
    reportType                  : HistoryReportTypeEnum.Asset,
    intervalInHours             : 2,
    inputEvents                 : [],
    assetSearchList             : [],
    initialAssets               : [],
    routeMatchingEnabled        : true,
    assetsSearchActive          : false,
    hideNonSelectedAssetsActive : true,
    error                       : {
      show       : false,
      assetCount : 0
    },
    persistLiveHistory : false
  },
  supplyChainDevices : {
    labelContent : {
      primaryLabel   : 'assetName',
      secondaryLabel : null
    },
    opacity       : 1,
    isLoaded      : false,
    isLabelActive : true,
    devices       : null,
    updates       : null,
    error         : null
  },
  assetDetailHistoryGrid : {
    history : {
      loading        : false,
      asset          : null,
      hoveredEvent   : null,
      history        : null,
      error          : null,
      startDate      : null,
      endDate        : null,
      filterLoading  : false,
      eventFilter    : null,
      expandedGroups : [],
      crashEvents    : {
        loading : false,
        results : null,
        error   : null
      }
    },
    grid : {
      columnState         : null,
      preferedColumnState : null,
      hasUpdate           : false
    }
  },
  tools : {
    disconnectedToolTagReport : null,
    disconnectedToolError     : null,
    selectedTool              : null,
    selectedToolParent        : null,
    toolsError                : null,
    toolsLoading              : false,
    toolPanelOpen             : []
  },
  assignedAssets : {
    assignedAssetsPanelsOpen : null
  },
  geofences : {
    savedGeofence  : null,
    savingGeofence : false,
    geofenceStatus : null,
    borderOnly     : false,
    geofenceLable  : false
  },
  fleetMode : {
    assetsAggregation         : [],
    error                     : null,
    loading                   : false,
    isLargeFleetMode          : false,
    largeFleetExtent          : null,
    refreshMap                : null,
    largeFleetAssetCount      : 0,
    isLargeFleetAssetsFetched : false,
    largeFleetAssetList       : {
      assets            : [],
      hasNextPage       : true,
      isNextPageLoading : false,
      refresh           : false,
      updateGrid        : false
    }
  },
  isMapSelection    : false,
  panMapOnly        : false,
  assetDetailView   : false,
  toolDetailView    : false,
  geoFencePanelOpen : false,
  searchHistory     : [],
  maps              : {
    mapLayerType : [
      MapLayerTypeEnum.AssetLayer,
      MapLayerTypeEnum.FleetAssetLayer,
      MapLayerTypeEnum.AssetHistoryLayer,
      MapLayerTypeEnum.TripHistoryLayer,
      MapLayerTypeEnum.EventLineLayer,
      MapLayerTypeEnum.OperationalLayer,
      MapLayerTypeEnum.GeofenceLayer,
      MapLayerTypeEnum.SupplyChainDeviceLayer,
      MapLayerTypeEnum.BoundaryLayer,
      MapLayerTypeEnum.MapContextMenuLayer

    ],
    mapLayerSettings : [
      MapLayerSettings.CurrentLayerSettings,
      MapLayerSettings.TripHistoryLayerSettings,
      MapLayerSettings.GeofenceLayerSettings,
      MapLayerSettings.TrafficLayerSettings,
      MapLayerSettings.WeatherLayerSettings,
      MapLayerSettings.OperationalLayersSettings,
      MapLayerSettings.SupplyChainDeviceLayerSettings
    ],
    isAssetDetailHistory    : false,
    initialized             : false,
    basemap                 : BaseMapEnum.osm,
    layers                  : ['current'],
    extent                  : null,
    initialExtent           : null,
    view                    : null,
    center                  : null,
    zoom                    : 6,
    trafficOpacity          : 1,
    trafficSubLayers        : [TRAFFIC_SUB_LAYER_LIVE_TRAFFIC],
    weatherOpacity          : 1,
    geofenceOpacity         : 0.5,
    coordinates             : {},
    resumeMapStatus         : false,
    operationalLayers       : [],
    isLayerListUpdate       : false,
    islayerVisibilityUpdate : false,
    extents                 : {
      extent        : null,
      initialExtent : null
    },
    isFleetLayerPersistenceActive : true
  },
  mobileMapControlsOpen : false,
  findButtonState       : false,
  // Temporary feature flag for panel detail items
  panelDetailFlags      : {
    issue       : false,
    tripInfo    : false,
    ionVision   : false,
    diagnostics : false,
    utilization : false,
    toolInfo    : false
  },
  findNearbyV2 : {
    isActive      : false,
    isPanelActive : false,
    refresh       : false,
    distance      : 10,
    fromItem      : null,
    results       : [],
    selectedAsset : null
  },
  findNearby : {
    isActive  : false,
    isLoading : false,
    asset     : null,
    geofence  : null,
    location  : null,
    results   : [],
    error     : null,
    label     : null,
    miles     : null,
    type      : null
  },
  findNearbyState : {
    showPopup : false,
    toggle    : false
  },
  search : {
    searchTerm : null,
    results    : null,
    selected   : null,
    history    : null,
    error      : null,
    isShowing  : false,
    isActive   : false,
    loading    : false
  },
  filters : {
    groups : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    assets : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    assetTypes : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    assetCategories : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    geoFences : {
      loading  : false,
      error    : null,
      items    : [],
      selected : [],
      open     : false
    },
    routes : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    assetStatus : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    date : {
      startDate : null,
      endDate   : null,
      period    : DateSelectorValues.Live
    },
    events : {
      selected : []
    },
    devices : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    boundary : []
  },
  isSelectedFiltersActive : false,
  appliedSaveFilter       : null,
  processedFilters        : [],
  featureFlags            : {
    showMapScaleWidget : true,
    panelDetailFlags   : {
      issue       : false,
      tripInfo    : false,
      ionVision   : false,
      diagnostics : false,
      utilization : false,
      toolInfo    : false
    }
  },
  accountMap         : null,
  selectedFilterName : '',
  isAllFiltersLoaded : true,
  sideSheetView      : SideSheetView.detailed,
  isMapOnlyView      : false,
  grid               : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {},
    state               : {},
    filteredAssetIds    : null,
    count               : 0
  },
  isMobileFilterActive : false,
  trackingHistory      : {
    routeHistory : {}
  },
  mapViews : {
    savedMapViews  : [],
    defaultMapView : null
  },
  isGeofenceLoaded : false,
  v2               : {
    fleetUpdateFlag         : false,
    fleetUpdateResetFlag    : true,
    fleetUpdateFetchingKeys : []
  },
  mapWidgetSelected     : null,
  contextMenu           : {},
  boundaryMode          : BoundaryMode.Boundary,
  isSnapToRoadEnabled   : true,
  isAssetUpdatesEnabled : true
};

const resetFindNearby = ({findNearby}: TrackingState) => {
  findNearby.error     = null;
  findNearby.asset     = null;
  findNearby.geofence  = null;
  findNearby.location  = null;
  findNearby.results   = null;
  findNearby.isActive  = false;
  findNearby.isLoading = false;
  findNearby.type      = null;
  findNearby.miles     = null;
};

const resetFindNearbyV2 = ({findNearbyV2}: TrackingState) => {
  findNearbyV2.isActive      = false;
  findNearbyV2.isPanelActive = false;
  findNearbyV2.refresh       = false;
  findNearbyV2.fromItem      = null;
  findNearbyV2.selectedAsset = null;
  findNearbyV2.results       = [];
};

const updateFindNearbyState = ({findNearby}: TrackingState, type: FindNearbyType, value) => {
  findNearby.type     = type;
  findNearby.asset    = null;
  findNearby.geofence = null;
  findNearby.location = null;
  findNearby[type]    = value;
};

const getAppliedFiltersCount = ({
  assetTypes,
  assetCategories,
  assetStatus,
  routes,
  groups,
  geoFences
}: TrackingFilters) => {
  return assetTypes?.selected?.length ||
    assetCategories?.selected?.length ||
    assetStatus?.selected?.length ||
    routes?.selected?.length ||
    groups?.selected?.length ||
    geoFences?.selected.length;
};

/**
 * Verifies the state of the search history and selected asset based on the latest search
 * result. If the given entry does not currently exist in the search history, it is added
 * as the first item, otherwise, if it does exist, it's state is updated to indicate that
 * it must be revalidated. When the history collection is greater than three, the oldest
 * item (last index) is remove. In addition, if the the result is an asset or driver, the
 * selected asset is updated to reflect the selection.
 */
const verifySearchResultHistory = ({
  search: {history},
  assets,
  history: breadcrumb,
  maps
}: TrackingState,
result: GlobalSearchResult) => {
  if (result && Object.keys(result).length > 1) {
    const {isAsset, isDriver, isFcDevice, isSciDevice} = result;
    if (isAsset || isDriver || isFcDevice || isSciDevice) {
      const {item: selectedAsset} = result || {};
      if (maps.layers.includes(HISTORY_LAYER)) {
        breadcrumb.selectedAsset = selectedAsset;
      } else {
        assets.selectedAsset = selectedAsset;
      }
    }
    if (result.item) {
      result.item = result?.item?.getDevice();
    }
    const itemIndex = history?.findIndex(({name}) => name === result.name) ?? -1;
    if (itemIndex === -1) {
      // only a new search makes room, dropping the oldest ...
      if (history?.length >= MAX_SEARCH_HISTORY_ENTRIES) {
        history.pop();
      }
      history.unshift(result);
    } else {
      // a repeated search moves back to the front, so the most recent
      // searches stay first - the legacy theme only shows the first
      // LEGACY_VISIBLE_SEARCH_HISTORY ...
      const [item] = history.splice(itemIndex, 1);
      if (item.type === result.type) {
        item.timestamp   = result.timestamp;
        item.revalidated = true;
      }
      history.unshift(item);
    }
    UserPreferences.set('tracking.search.history', history.map(entry => {
      return {...entry, revalidated: false};
    }), true);
  }
};

const exclude = (items: string[], name: string) => items.filter(geofence => geofence !== name);

// Update the Operational Layers state
const updateOperationLayer = ({
  operationalLayers,
  prop,
  updated,
  field,
  isSublayer
}: UpdateOperationLayer): GenericObject[] => {
  operationalLayers?.forEach((layer) => {
    const {title} = layer;
    if (title === updated?.title || title === updated?.layerTitle) {
      if (isSublayer) {
        const sublayer = layer?.layers?.find(({id}) => id === updated?.sublayerId);
        if (sublayer) {
          sublayer[prop] = updated[field];
        }
      } else {
        layer[prop] = updated[field];
      }
    }
  });
  return operationalLayers;
};

// Update the Operational Layers state
const updateSavedOperationLayers = (operationalLayers, savedOperationalLayers): GenericObject[] => {
  operationalLayers?.forEach((operationalLayer) => {
    const {id: operationalLayerId} = operationalLayer;
    const {id, layers, opacity} = savedOperationalLayers?.find(({id}) => id === operationalLayerId) || {};
    if (id) {
      if (opacity >= 0) {
        operationalLayer.opacity = opacity;
      }
      operationalLayer.layers = layers;
    }
    operationalLayer.visibility = !!id;
  });
  return operationalLayers;
};

// excludeLayer method helps to exclude layer from the layer collection
const excludeLayer = (layers, layer) => layers?.filter(l => l !== layer);

// defines the preference key for the tracking assets page ...
const PREFERENCE = 'tracking-assets';

// defines the preference key for the tracking page's asset detail history section ...
const ASSET_DETAIL_HISTORY_PREFERENCE = 'tracking-assetDetailHistory';

// defines a utility to return basemap from default MapView or extendedAttributes.
const getBasemap = (defaultSavedMapView) => {
  return defaultSavedMapView?.basemap || getDefaultBasemap();
};

/**
 * Provides a convenience function for determining if Page Monitoring is currently
 * active. If so, and if the current Page being Monitored is Tracking 2, then Page
 * Monitoring will automatically be marked as completed.
 */
const onPageMonitorComplete = () => {
  const monitor = getMonitor();
  if (monitor?.isRunning()) {
    if (contains(monitor?.context, Route.TRACKING)) {
      monitor.stop();
    }
  }
};

// defines the reducers ...
const reducers = {
  [APPLICATION_INITIALIZATION_COMPLETE] : ({search, maps, mapViews}: TrackingState) => {
    let history               = UserPreferences.get('tracking.search.history') || [];
    const savedMapViews       = UserPreferences.get(Preference.savedMapViews) || [];
    const defaultSavedMapView = getDefaultSavedMapView(savedMapViews);
    if (history?.length > MAX_SEARCH_HISTORY_ENTRIES) {
      history = history?.slice(0, MAX_SEARCH_HISTORY_ENTRIES);
    }
    search.history         = history;
    mapViews.savedMapViews = savedMapViews;
    maps.basemap           = getBasemap(defaultSavedMapView);
    if (Features.hasSCIAccess()) {
      maps.layers.push('scDevice');
    }
  },
  [INITIALIZE_TRACKING] : (draft: TrackingState, {assets}) => {
    const savedMapViews               = UserPreferences.get(Preference.savedMapViews) || [];
    const defaultSavedMapView         = getDefaultSavedMapView(savedMapViews);
    draft.isInitialized               = true;
    draft.fleetMode.assetsAggregation = [];
    draft.assets.assets               = assets;
    draft.maps.basemap                = getBasemap(defaultSavedMapView);
  },
  [INITIALIZE_OPERATIONAL_LAYERS] : ({maps}: TrackingState, {operationalLayers}) => {
    maps.isLayerListUpdate = false;
    maps.operationalLayers = operationalLayers;
  },
  [SET_OPERATIONAL_LAYER_VISIBILITY] : ({maps}: TrackingState, {updated}) => {
    const data = {
      operationalLayers : maps.operationalLayers,
      prop              : 'visibility',
      field             : 'visible',
      updated
    };
    maps.isLayerListUpdate       = true;
    maps.islayerVisibilityUpdate = !maps?.islayerVisibilityUpdate;
    maps.operationalLayers       = updateOperationLayer(data as UpdateOperationLayer);
  },
  [SET_OPERATIONAL_LAYER_OPACITY] : ({maps}: TrackingState, {updated}) => {
    const data = {
      operationalLayers : maps.operationalLayers,
      prop              : 'opacity',
      field             : 'opacity',
      updated
    };
    maps.isLayerListUpdate = true;
    maps.operationalLayers = updateOperationLayer(data as UpdateOperationLayer);
  },
  [SET_OPERATIONAL_SUBLAYER_VISIBILITY] : ({maps}: TrackingState, {updated}) => {
    const data = {
      operationalLayers : maps.operationalLayers,
      prop              : 'defaultVisibility',
      field             : 'visible',
      isSublayer        : true,
      updated
    };
    maps.isLayerListUpdate = true;
    maps.operationalLayers = updateOperationLayer(data as UpdateOperationLayer);
  },
  [SET_OPERATIONAL_LAYERS] : ({maps}: TrackingState, {updated}) => {
    maps.isLayerListUpdate = true;
    maps.operationalLayers = updateSavedOperationLayers(maps.operationalLayers, updated);
  },
  [SET_IS_LAYER_LIST_UPDATE] : ({maps}: TrackingState, {value}) => {
    maps.isLayerListUpdate = value;
  },
  [RESET_TRACKING] : (draft: TrackingState) => {
    draft.isInitialized               = false;
    draft.assets.assets               = null;
    draft.findNearbyState.showPopup   = false;
    draft.trackingMode                = TrackingMode.Live;
    draft.timeMode                    = TimeModes.live;
    draft.isTimeModePanelOpen         = false;
    draft.supplyChainDevices.devices  = [];
    draft.supplyChainDevices.updates  = [];
    draft.supplyChainDevices.isLoaded = false;
  },
  [MAP_INITIALIZED] : (draft: TrackingState) => {
    draft.maps.initialized = true;
    onPageMonitorComplete();
  },
  [SET_AUTO_REFRESH_STATE] : (draft: TrackingState, {isAutoRefresh}) => {
    if (!isNil(isAutoRefresh)) {
      draft.assets.isAutoRefresh = isAutoRefresh;
    }
  },
  [SET_ASSET_GRID_UPDATE] : ({grid}: TrackingState, {hasUpdate}) => {
    grid.hasUpdate = hasUpdate;
  },
  [TRACKING_ASSETS_REQUEST] : (draft: TrackingState) => {
    draft.assets.loading = true;
  },
  [TRACKING_ASSETS_RESPONSE] : (draft: TrackingState) => {
    draft.assets.loading = false;
  },
  [TRACKING_SEARCH_REQUEST] : ({search}: TrackingState, {searchTerm}) => {
    search.searchTerm = searchTerm;
    search.loading    = true;
    search.results    = null;
  },
  [TRACKING_SEARCH_RESPONSE] : ({search}: TrackingState, {results}) => {
    search.loading = false;
    search.results = {
      ...results
    };
  },
  [TRACKING_SEARCH_ERROR] : ({search}: TrackingState, {error}) => {
    search.loading = false;
    search.error   = error;
  },
  [SET_GEOFENCE_BORDER_ONLY] : ({geofences}: TrackingState, {borderOnly}) => {
    geofences.borderOnly = borderOnly;
  },
  [SET_GEOFENCE_LABEL_STATE] : ({geofences}: TrackingState, {labelState}) => {
    geofences.geofenceLable = labelState;
  },
  [SET_SELECTED_TRACKING_SEARCH_RESULT] : (draft: TrackingState, {result}) => {
    verifySearchResultHistory(draft, result);
    draft.isMapSelection  = false;
    draft.panMapOnly      = false;
    draft.search.selected = result;
  },
  [SET_HOVERED_ASSET] : ({assets: {hoveredAsset}}: TrackingState, {asset, source}) => {
    hoveredAsset.asset = asset;
    hoveredAsset.source = source;
  },
  [SET_SELECTED_ASSET] : (draft: TrackingState, {asset, isMapSelection, panMapOnly}) => {
    draft.assets.selectedAsset = asset;
    draft.isMapSelection = !isNil(isMapSelection);
    draft.panMapOnly     = isTrue(panMapOnly);
  },
  [SET_LAST_SELECTED_ASSET] : (draft: TrackingState, {asset}) => {
    draft.assets.lastSelectedAsset = asset;
  },
  [SET_PANEL_TOP_POSITION] : (draft: TrackingState, {position}) => {
    draft.assets.panelTopPosition = position;
  },
  [GET_ASSET_DETAILS_REQUEST] : (draft: TrackingState) => {
    draft.assets.assetDetailsLoading = true;
  },
  [SET_ASSET_DETAILS_LOADING] : (draft: TrackingState, {value}) => {
    draft.assets.assetDetailsLoading = value;
  },
  [GET_ASSET_DETAILS_RESPONSE] : (draft: TrackingState, {asset, isMapSelection}) => {
    if (!isNil(isMapSelection)) {
      draft.isMapSelection = isMapSelection;
      draft.panMapOnly     = false;
    }
    draft.assets.assetDetailsLoading = false;
    draft.assets.selectedAsset       = asset;
  },
  [GET_ASSET_DETAILS_ERROR] : (draft: TrackingState, {err}) => {
    draft.assets.assetDetailsLoading = false;
    draft.assets.assetDetailsError   = err;
  },
  [SET_TOOL_PANEL_OPEN] : (draft: TrackingState, state) => {
    draft.tools.toolPanelOpen = [...state.newState];
  },
  [SET_ASSIGNED_ASSETS_PANEL_OPEN] : (draft: TrackingState, {openPanels}) => {
    draft.assignedAssets.assignedAssetsPanelsOpen = openPanels;
  },
  [SET_ASSET_DETAIL_VIEW] : (draft: TrackingState, {value}) => {
    draft.assetDetailView = value;
  },
  [SET_TRACKING_BASE_MAP] : ({maps}: TrackingState, {basemap}) => {
    maps.basemap = basemap;
  },
  [SET_TRACKING_MAP_LAYERS] : ({maps}: TrackingState, {layers}) => {
    maps.layers = uniq(layers);
  },
  [SET_TRAFFIC_LAYER_OPACITY] : ({maps}: TrackingState, {opacity}) => {
    maps.trafficOpacity = opacity;
  },
  [SET_TRAFFIC_LAYER_SUBLAYERS] : ({maps}: TrackingState, {trafficSubLayers}) => {
    maps.trafficSubLayers = trafficSubLayers;
  },
  [SET_WEATHER_LAYER_OPACITY] : ({maps}: TrackingState, {opacity}) => {
    maps.weatherOpacity = opacity;
  },
  [SET_GEOFENCE_LAYER_OPACITY] : ({maps}: TrackingState, {opacity}) => {
    maps.geofenceOpacity = opacity;
  },
  [SET_TRACKING_MAP_POSITION] : ({maps}: TrackingState, {zoom, extent, center}) => {
    maps.zoom   = zoom;
    maps.extent = extent;
    maps.center = center;
  },
  [SET_TRACKING_MAP_VIEW] : ({maps}: TrackingState, {state}) => {
    maps.view = state;
  },
  [SET_TRACKING_MAP_ZOOM] : ({maps}: TrackingState, {zoom}) => {
    maps.zoom = zoom;
  },
  [SET_TRACKING_MAP_EXTENT] : ({maps}: TrackingState, {extent}) => {
    maps.extent = extent;
  },
  [SET_INITIAL_TRACKING_MAP_EXTENT] : ({maps}: TrackingState, {extent}) => {
    maps.initialExtent = extent;
  },
  [SET_TRACKING_MAPVIEW_INITIAL_EXTENT] : ({maps}: TrackingState, {extent}) => {
    maps.extents.initialExtent = extent;
  },
  [SET_TRACKING_MAPVIEW_EXTENT] : ({maps}: TrackingState, {extent}) => {
    maps.extents.extent = extent;
  },
  [SET_STATUS_TO_RESUME_MAP] : ({maps}: TrackingState, {status}) => {
    maps.resumeMapStatus = status;
  },
  [SET_TOOL_DETAIL_VIEW] : (draft: TrackingState, {value}) => {
    draft.toolDetailView = value;
  },
  [SET_SELECTED_TOOL] : (draft: TrackingState, {tool}) => {
    draft.tools.selectedTool = tool;
  },
  [SET_DISCONNECTED_TOOL_TAG_REPORT] : ({tools}: TrackingState, {tagReport}) => {
    tools.disconnectedToolTagReport = tagReport;
  },
  [SET_DISCONNECTED_TOOL_ERROR] : ({tools}: TrackingState, {err}) => {
    tools.disconnectedToolError = err;
  },
  [SET_GEOFENCE_PANEL_OPEN] : (draft: TrackingState, {geoFencePanelOpen}) => {
    draft.geoFencePanelOpen = geoFencePanelOpen;
  },
  [SET_FIND_NEARBY_ACTIVE_STATUS] : ({findNearbyV2}: TrackingState, {isActive}) => {
    findNearbyV2.isActive = isActive;
    if (!isActive) {
      findNearbyV2.refresh = false;
      findNearbyV2.results = [];
    }
  },
  [SET_FIND_NEARBY_PANEL_ACTIVE] : (draft: TrackingState, {isActive}) => {
    draft.findNearbyV2.isPanelActive = isActive;
    if (!isActive) {
      resetFindNearbyV2(draft);
    }
  },
  [SET_FIND_NEARBY_DISTANCE] : ({findNearbyV2}: TrackingState, {distance}) => {
    findNearbyV2.distance = distance;
  },
  [SET_FIND_NEARBY_RESULTS] : ({findNearbyV2}: TrackingState, {results}) => {
    findNearbyV2.results = results;
  },
  [SET_FIND_NEARBY_FROM_ITEM] : ({findNearbyV2}: TrackingState, {fromItem}) => {
    findNearbyV2.selectedAsset = null;
    findNearbyV2.fromItem = fromItem;
  },
  [SET_FIND_NEARBY_REFRESH] : ({findNearbyV2}: TrackingState, {refresh}) => {
    findNearbyV2.refresh = refresh;
  },
  [SET_FIND_NEARBY_SELECTED_ASSET] : ({findNearbyV2}: TrackingState, {selectedAsset}) => {
    findNearbyV2.selectedAsset = selectedAsset;
  },
  [FIND_NEARBY_REQUEST] : ({findNearby}: TrackingState, {miles, label, findType}) => {
    findNearby.results     = [];
    findNearby.error       = null;
    findNearby.isActive    = true;
    findNearby.isLoading   = true;
    findNearby.label       = label;
    findNearby.miles       = miles;
    findType && (findNearby.type = findType);
  },
  [FIND_NEARBY_RESPONSE] : ({findNearby}: TrackingState, {results}) => {
    findNearby.results   = results;
    findNearby.isLoading = false;
  },
  [FIND_NEARBY_ERROR] : ({findNearby}: TrackingState, {error}) => {
    findNearby.error     = error;
    findNearby.isLoading = false;
  },
  [EXIT_FIND_NEARBY] : (draft: TrackingState) => {
    draft.assets.isAutoRefresh = false;
    resetFindNearby(draft);
  },
  [GET_ASSET_TOOLS_REQUEST] : ({tools}: TrackingState, {asset}) => {
    tools.toolsError = null;
    tools.toolsLoading = true;
    tools.selectedToolParent = asset.id;
  },
  [GET_ASSET_TOOLS_RESPONSE] : (draft: TrackingState, {assetId, tools}) => {
    draft.tools.toolsLoading = false;
    draft.assets.selectedAssetTools = [...draft.assets.selectedAssetTools, {assetId, tools}];
  },
  [GET_ASSET_TOOLS_ERROR] : ({assets, tools}: TrackingState, {error}) => {
    tools.toolsLoading = false;
    assets.selectedAssetTools = [];
    tools.toolsError = error;
  },
  [GET_ASSOCIATED_ASSETS_REQUEST] : ({assets: {associatedAssets}}: TrackingState) => {
    associatedAssets.error = null;
    associatedAssets.isLoading = true;
  },
  [GET_ASSOCIATED_ASSETS_RESPONSE] : (
    {assets:{associatedAssets: currentAssociatedAssets}}: TrackingState, {assetId, associatedAssets}) => {
    currentAssociatedAssets.isLoading = false;
    const indexIfExists = currentAssociatedAssets.assets.findIndex(assetObject => assetObject.assetId === assetId);
    if (indexIfExists > -1) {
      currentAssociatedAssets.assets.splice(indexIfExists, 1, {assetId, associatedAssets});
    } else {
      currentAssociatedAssets.assets = [...currentAssociatedAssets.assets, {assetId, associatedAssets}];
    }
  },
  [GET_ASSOCIATED_ASSETS_ERROR] : ({assets:{associatedAssets}}: TrackingState, {error, defaultAssets}) => {
    associatedAssets.isLoading = false;
    associatedAssets.error = error;
    if (defaultAssets) {
      associatedAssets.assets = defaultAssets;
    }
  },
  [SET_GROUPS_FILTER_REQUEST] : ({filters:{groups}}: TrackingState) => {
    groups.error    = null;
    groups.loading  = true;
  },
  [SET_GROUPS_FILTER_RESPONSE] : (draft: TrackingState, {value}) => {
    draft.filters.groups.loading  = false;
    draft.filters.groups.selected = value;
    draft.isSelectedFiltersActive = true;
  },
  [SET_GROUPS_FILTER_ERROR] : ({filters:{groups}}: TrackingState, {error}) => {
    groups.error     = error;
    groups.loading   = false;
    groups.selected  = [];
  },
  [SET_ASSETS_FILTER_REQUEST] : ({filters:{assets}}: TrackingState) => {
    assets.error    = null;
    assets.loading  = true;
  },
  [SET_ASSETS_FILTER_RESPONSE] : (draft: TrackingState, {value}) => {
    draft.filters.assets.loading  = false;
    draft.filters.assets.selected = value;
    draft.isSelectedFiltersActive = true;
  },
  [SET_ASSETS_FILTER_ERROR] : ({filters:{assets}}: TrackingState, {error}) => {
    assets.error     = error;
    assets.loading   = false;
    assets.selected  = [];
  },
  [SET_DEVICES_FILTER_REQUEST] : ({filters:{devices}}: TrackingState) => {
    devices.error    = null;
    devices.loading  = true;
  },
  [SET_DEVICES_FILTER_RESPONSE] : (draft: TrackingState, {value}) => {
    draft.filters.devices.loading  = false;
    draft.filters.devices.selected = value;
    draft.isSelectedFiltersActive = true;
  },
  [SET_DEVICES_FILTER_ERROR] : ({filters:{devices}}: TrackingState, {error}) => {
    devices.error     = error;
    devices.loading   = false;
    devices.selected  = [];
  },
  [SET_ASSET_TYPE_FILTER_REQUEST] : ({filters:{assetTypes}}: TrackingState) => {
    assetTypes.error    = null;
    assetTypes.loading  = true;
  },
  [SET_ASSET_TYPE_FILTER_RESPONSE] : (draft: TrackingState, {value}) => {
    draft.filters.assetTypes.loading  = false;
    draft.filters.assetTypes.selected = value;
    draft.isSelectedFiltersActive = true;
  },
  [SET_ASSET_TYPE_FILTER_ERROR] : ({filters:{assetTypes}}: TrackingState, {error}) => {
    assetTypes.error    = error;
    assetTypes.loading  = false;
    assetTypes.selected = [];
  },
  [SET_CATEGORY_FILTER_REQUEST] : ({filters:{assetCategories}}: TrackingState) => {
    assetCategories.error    = null;
    assetCategories.loading  = true;
  },
  [SET_CATEGORY_FILTER_RESPONSE] : (draft: TrackingState, {value}) => {
    draft.filters.assetCategories.loading  = false;
    draft.filters.assetCategories.selected = value;
    draft.isSelectedFiltersActive = true;
  },
  [SET_CATEGORY_FILTER_ERROR] : ({filters:{assetCategories}}: TrackingState, {error}) => {
    assetCategories.error    = error;
    assetCategories.loading  = false;
    assetCategories.selected = [];
  },
  [SET_ROUTE_FILTER_REQUEST] : ({filters:{routes}}: TrackingState) => {
    routes.error   = null;
    routes.loading = true;
  },
  [SET_ROUTE_FILTER_RESPONSE] : (draft: TrackingState, {value}) => {
    draft.filters.routes.loading  = false;
    draft.filters.routes.selected = value;
    draft.isSelectedFiltersActive = true;
  },
  [SET_ROUTE_FILTER_ERROR] : ({filters:{routes}}: TrackingState, {error}) => {
    routes.error    = error;
    routes.loading  = false;
    routes.selected = [];
  },
  [SET_GEOFENCE_FILTER_OPEN] : ({filters:{geoFences}}: TrackingState, {value}) => {
    geoFences.open    = value;
  },
  [SET_GEOFENCE_FILTER_REQUEST] : ({filters:{geoFences}}: TrackingState) => {
    geoFences.error    = null;
    geoFences.loading  = true;
  },
  [SET_GEOFENCE_FILTER_RESPONSE] : (draft: TrackingState, {value}) => {
    draft.filters.geoFences.loading  = false;
    draft.filters.geoFences.selected = value;
    draft.isSelectedFiltersActive = true;
  },
  [SET_GEOFENCE_FILTER_ERROR] : ({filters:{geoFences}}: TrackingState, {error}) => {
    geoFences.error     = error;
    geoFences.loading   = false;
    geoFences.selected  = [];
  },
  [SET_ASSET_STATUS_FILTER_REQUEST] : ({filters:{assetStatus}}: TrackingState) => {
    assetStatus.error    = null;
    assetStatus.loading  = true;
  },
  [SET_ASSET_STATUS_FILTER_RESPONSE] : (draft: TrackingState, {value}) => {
    draft.filters.assetStatus.loading  = false;
    draft.filters.assetStatus.selected = value;
    draft.isSelectedFiltersActive = true;
  },
  [SET_ASSET_STATUS_FILTER_ERROR] : ({filters:{assetStatus}}: TrackingState, {error}) => {
    assetStatus.error     = error;
    assetStatus.loading   = false;
    assetStatus.selected  = [];
  },
  [SET_SAVE_FILTER_REQUEST] : (draft: TrackingState, {value: {filterName, filterState}}) => {
    draft.filters           = filterState;
    draft.appliedSaveFilter = filterName;
  },
  [SET_SAVE_FILTER_ERROR] : (draft: TrackingState) => {
    draft.filters           = initialState.filters;
    draft.appliedSaveFilter = null;
  },
  [SET_ALL_FILTERS_LOADING_STATE] : (draft, {status}) => {
    draft.isAllFiltersLoaded = status;
  },
  [SET_SIDESHEET_VIEW] : (draft: TrackingState, {viewType}) => {
    draft.sideSheetView             = viewType;
    draft.findNearbyState.showPopup = false;
  },
  [SET_MAP_ONLY_VIEW] : (draft: TrackingState, {isMapOnlyView}) => {
    draft.isMapOnlyView = isMapOnlyView;
  },
  [APPLY_GEOFENCE_TO_FILTER] : ({filters:{geoFences}}: TrackingState,
    {
      name,
      updatedName,
      id,
      isDelete,
      isUpdate
    }) => {
    if (isDelete) {
      geoFences.items = [...exclude(geoFences.items, name)];
      if (geoFences.selected?.indexOf(name) > -1) {
        geoFences.selected = [...exclude(geoFences.selected, name)];
      }
    } else if (isUpdate) {
      geoFences.items.splice(geoFences.items?.indexOf(updatedName), 1, name);
      if (geoFences.selected?.indexOf(updatedName) > -1) {
        geoFences.selected = [...exclude(geoFences.selected, updatedName), name];
      }
    } else {
      geoFences.items = [...geoFences.items, {name, id}];
    }
  },
  [SAVE_GEOFENCE] : ({geofences}: TrackingState, {savedGeofence}) => {
    geofences.savedGeofence = savedGeofence;
  },
  [SAVING_GEOFENCE] : ({geofences}: TrackingState, {value}) => {
    geofences.savingGeofence = value;
  },
  [SAVE_GEOFENCE_SUCCESS] : ({geofences}: TrackingState, {status}) => {
    geofences.geofenceStatus = {success: true, ...status};
  },
  [SAVE_GEOFENCE_ERROR] : ({geofences}: TrackingState, {status}) => {
    geofences.geofenceStatus = {success: false, ...status};
  },
  [RESET_GEOFENCE_STATUS] : ({geofences}: TrackingState, {geofenceStatus}) => {
    geofences.geofenceStatus = geofenceStatus;
  },
  [TOGGLE_MAP_CONTROLS] : (draft, {status}) => {
    draft.mobileMapControlsOpen = status;
  },
  [TOGGLE_ASSET_CLUSTERING] : ({assets}, {status, fromMap}) => {
    if (!fromMap) {
      if (isNil(assets.mapClustering)) {
        assets.clustering = status;
      }
    } else {
      assets.mapClustering = status;
    }
  },
  [TOGGLE_ASSET_LABELLING] : ({assets}, {status}) => {
    assets.labelling = status;
  },
  [SET_ASSET_CONTENT] : ({assets}, {labelContent}) => {
    assets.labelContent = labelContent;
  },
  [SET_FIND_NEARBY_POPUP_STATE] : ({findNearbyState}, {status = false}) => {
    findNearbyState.showPopup = status;
  },
  [SET_FIND_NEARBY_TOGGLE] : ({findNearbyState}) => {
    findNearbyState.toggle = !findNearbyState.toggle;
  },
  [SET_FIND_NEARBY_ASSET] : (draft: TrackingState, {value}) => {
    updateFindNearbyState(draft, FindNearbyType.asset, value);
  },
  [SET_FIND_NEARBY_GEOFENCE] : (draft: TrackingState, {value}) => {
    updateFindNearbyState(draft, FindNearbyType.geofence, value);
  },
  [SET_LOCATION_FIND_NEARBY] : (draft: TrackingState, {value}) => {
    updateFindNearbyState(draft, FindNearbyType.location, value);
  },
  [SET_PROCESSED_FILTERS] : (draft, {value}) => {
    draft.processedFilters = value;
  },
  [SET_FILTER_NAME] : (draft, {value}) => {
    draft.selectedFilterName = value;
  },
  [SET_TRACKING_FILTERS] : (draft, {value}) => {
    draft.filters = value;
  },
  [SET_FIND_NEARBY_LABEL] : ({findNearbyLabel}, {label, value}) => {
    findNearbyLabel.label = label;
    findNearbyLabel.value = value;
  },
  [SET_FIND_BUTTON_STATE] : (draft: TrackingState, {status}) => {
    draft.findButtonState = status;
  },
  [SET_SEARCH_HISTORY] : (draft, {searchHistory}) => {
    const newPlacesWithoutDuplicate = removeDuplicate('magicKey',
      [searchHistory, ...draft.searchHistory]);
    draft.searchHistory = newPlacesWithoutDuplicate;
  },
  [SET_GROUP_STATUS] : (draft: TrackingState, {value}) => {
    draft.accountMap = value;
  },
  [GET_ASSETS_HISTORY_REQUEST] : ({history}: TrackingState, {loading}) => {
    history.loading = loading;
  },
  [GET_ASSETS_HISTORY_RESPONSE] : ({history}: TrackingState, {assets}) => {
    history.loading     = false;
    history.assets      = assets;
    history.updatesLive = true;
  },
  [GET_ASSETS_HISTORY_ERROR] : ({history}: TrackingState, {loading}) => {
    history.loading = loading;
  },
  [RESET_ASSETS_HISTORY] : ({history, maps}: TrackingState) => {
    history.assets      = [];
    history.inputEvents = [];
    maps.layers         = excludeLayer(maps.layers, HISTORY_LAYER);
  },
  [SET_ASSETS_HISTORY_ERROR] : ({history, maps}: TrackingState, {showError, assetCount}) => {
    history.error.show       = showError;
    history.error.assetCount = assetCount;
    history.loading          = false;
    if (showError) {
      maps.layers = excludeLayer(maps.layers, HISTORY_LAYER);
    }
  },
  [SET_ASSETS_HISTORY_UPDATES_LIVE] : ({history}: TrackingState, {status}) => {
    history.updatesLive = status;
  },
  [SET_ASSETS_HISTORY_LIVE_INTERVAL] : ({history}: TrackingState, {hours}) => {
    history.intervalInHours = hours;
  },
  [SET_HISTORY_REFRESH] : ({history}: TrackingState, {refresh}) => {
    history.isRefresh = refresh;
  },
  [SET_ASSETS_HISTORY_REPORT_TYPE] : ({history}: TrackingState, {reportType}) => {
    history.reportType = reportType;
  },
  [SET_ASSETS_HISTORY_INPUT_EVENTS] : ({history}: TrackingState, {inputEvents}) => {
    history.inputEvents = inputEvents;
  },
  [SHOW_ASSET_HISTORY_REFRESH_MESSAGE] : ({history}: TrackingState, {showMessage}) => {
    history.showRefreshMessage = showMessage;
    if (!showMessage) {
      history.assets      = [];
      history.inputEvents = [];
    }
  },
  [SET_HISTORY_SELECTED_ASSET] : ({history}: TrackingState, {selectedAsset}) => {
    history.selectedAsset = selectedAsset;
  },
  [RESET_LARGE_FLEET_STATE] : ({fleetMode}: TrackingState) => {
    fleetMode.largeFleetAssetList.assets = [];
  },
  [SET_LARGE_FLEET_ASSETS_AGGREGATION_LEVELS] : ({fleetMode, assets}: TrackingState, {assetsAggregation}) => {
    fleetMode.assetsAggregation = assetsAggregation;
    fleetMode.refreshMap        = false;
    if (assetsAggregation?.length === 1 && !assetsAggregation?.features?.length) {
      assets.assetsLoading = false;
    }
  },
  [REFRESH_LARGE_FLEET_STATE] : ({fleetMode}: TrackingState, {refresh}) => {
    fleetMode.largeFleetAssetList.refresh = refresh;
  },
  [GET_LARGE_FLEET_GRID_REQUEST] : ({fleetMode}: TrackingState) => {
    fleetMode.loading = true;
  },
  [GET_LARGE_FLEET_GRID_RESPONSE] : ({fleetMode}: TrackingState) => {
    fleetMode.loading = false;
  },
  [GET_LARGE_FLEET_GRID_ERROR] : ({fleetMode}: TrackingState, {error}) => {
    fleetMode.error = error;
    fleetMode.loading = false;
  },
  [SET_LARGE_FLEET_STATE] : (draft: TrackingState, {largeFleetMode, response, refreshMap}) => {
    if (largeFleetMode) {
      draft.assets.assets = null;
    } else {
      draft.assets.initialExtent            = response.count ? response.extent : null;
      const {largeFleetAssetList}           = draft.fleetMode;
      largeFleetAssetList.assets            = [];
      largeFleetAssetList.hasNextPage       = false;
      largeFleetAssetList.isNextPageLoading = false;
    }
    draft.assets.assetsLoading              = !draft.isInitialized;
    draft.isInitialized                     = true;
    draft.fleetMode.assetsAggregation       = [];
    draft.fleetMode.isLargeFleetMode        = largeFleetMode;
    draft.fleetMode.largeFleetAssetCount    = largeFleetMode ? response.count : 0;
    draft.fleetMode.largeFleetExtent        = largeFleetMode ? response.extent : null;
    draft.fleetMode.refreshMap              = refreshMap;
  },
  [GET_LARGE_FLEET_REQUEST] : ({fleetMode}: TrackingState) => {
    fleetMode.largeFleetAssetList.isNextPageLoading = true;
  },
  [GET_LARGE_FLEET_RESPONSE] : ({fleetMode, filters, sideSheetView}: TrackingState,
    {assets, isOverride = false, updateGrid = false}) => {
    const {largeFleetAssetCount, largeFleetAssetList} = fleetMode;
    const {assets : state}                = largeFleetAssetList;
    const allAssets                       = isOverride
      ? assets
      : [...state, ...assets];
    const allAssetsLength                 = allAssets?.length;
    largeFleetAssetList.assets            = allAssets;
    largeFleetAssetList.hasNextPage       = allAssetsLength < largeFleetAssetCount;
    largeFleetAssetList.isNextPageLoading = false;
    largeFleetAssetList.updateGrid        = updateGrid;
    if (allAssetsLength > largeFleetAssetCount &&
        getAppliedFiltersCount(filters) === 0) {
      fleetMode.largeFleetAssetCount = allAssetsLength;
    }
    fleetMode.isLargeFleetAssetsFetched = true;
  },
  [GET_LARGE_FLEET_ERROR] : ({fleetMode}: TrackingState) => {
    fleetMode.largeFleetAssetList.isNextPageLoading = false;
  },
  [RESET_FETCH_LARGE_FLEET_ASSETS] : ({fleetMode}: TrackingState) => {
    fleetMode.isLargeFleetAssetsFetched = false;
  },
  // Table View grid
  [INITIALIZE_TRACKING_ASSETS] : draft => {
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [RE_INITIALIZE_TRACKING] : draft => {
    reInitializeState(draft, {
      initialState,
      states : [
        'trackingMode',
        'search',
        'maps',
        'mapViews'
      ]
    });
  },
  // Table View grid
  [SET_GRID_COLUMNS] : ({grid, assets}: TrackingState, {columnState}) => {
    onSetGridColumns({
      isLoading  : assets.loading,
      preference : PREFERENCE,
      grid
    }, columnState);
  },
  // Table View grid
  [UPDATE_GRID_COLUMNS] : ({grid}: TrackingState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  // Table View grid
  [SET_GRID_FILTERED_ASSET_IDS] : ({grid}: TrackingState, {assetIds}) => {
    grid.filteredAssetIds = assetIds;
  },
  [LOAD_SMARTER_TRAILER_REQUEST] : ({assets: {smarterTrailer}}: TrackingState) => {
    smarterTrailer.loading = true;
  },
  [LOAD_SMARTER_TRAILER_RESPONSE] : ({assets: {smarterTrailer}}: TrackingState, {trailer}) => {
    smarterTrailer.loading              = false;
    smarterTrailer.selectedAssetTrailer = trailer;
    smarterTrailer.error                = null;
  },
  [LOAD_SMARTER_TRAILER_ERROR] : ({assets: {smarterTrailer}}: TrackingState, {err}) => {
    smarterTrailer.loading              = false;
    smarterTrailer.selectedAssetTrailer = null;
    smarterTrailer.error                = err;
  },
  [UPDATE_SMARTER_TRAILER_REQUEST] : ({assets: {smarterTrailer}}: TrackingState) => {
    smarterTrailer.sensorUpdateLoading = true;
  },
  [UPDATE_SMARTER_TRAILER_RESPONSE] : ({assets: {smarterTrailer}}: TrackingState, {trailer}) => {
    smarterTrailer.sensorUpdateLoading = false;
    smarterTrailer.selectedAssetTrailer = trailer;
  },
  [UPDATE_SMARTER_TRAILER_ERROR] : ({assets: {smarterTrailer}}: TrackingState, {err}) => {
    smarterTrailer.sensorUpdateLoading = false;
    smarterTrailer.selectedAssetTrailer = null;
    smarterTrailer.error = err;
  },
  [ADD_DEVICE_WITH_SMARTER_TRAILER] : ({assets: {smarterTrailer}}: TrackingState, {esn, apps}) => {
    smarterTrailer.devicesHaveTrailerSensors.push({esn, apps});
  },
  [SMARTER_TRAILER_CHECKED] : ({assets: {smarterTrailer}}: TrackingState, {esn}) => {
    smarterTrailer.devicesChecked.push(esn);
  },
  [SMARTER_TRAILER_SENSOR_NEEDS_UPDATE] : ({assets: {smarterTrailer}}: TrackingState, {value}) => {
    smarterTrailer.sensorNeedsUpdate = value;
  },
  [TOGGLE_MOBILE_FILTERS] : (draft: TrackingState, {show}) => {
    draft.isMobileFilterActive = show;
  },
  [SET_TRACKING_HISTORY_REPORT] : ({trackingHistory}: TrackingState, {routeHistory}) => {
    trackingHistory.routeHistory = routeHistory;
  },
  [INITIALIZE_ASSET_DETAIL_HISTORY] : ({assetDetailHistoryGrid: {grid}}) => {
    const preference         = getGridColumnState(ASSET_DETAIL_HISTORY_PREFERENCE, true);
    grid.columnState         = preference;
    grid.preferedColumnState = preference;
  },
  [LOAD_ASSET_DETAIL_HISTORY_REQUEST] : ({assetDetailHistoryGrid: {history}}: TrackingState) => {
    history.error   = null;
    history.loading = true;
  },
  [LOAD_ASSET_DETAIL_HISTORY_RESPONSE] : ({assetDetailHistoryGrid: {history}}: TrackingState, {history: allHist}) => {
    history.history       = allHist;
    history.loading       = false;
    history.filterLoading = false;
  },
  [LOAD_ASSET_DETAIL_HISTORY_ERROR] : ({assetDetailHistoryGrid: {history}}: TrackingState, {error}) => {
    history.error         = error;
    history.loading       = false;
    history.filterLoading = false;
  },
  [LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_REQUEST] : ({assetDetailHistoryGrid: {history}}: TrackingState) => {
    history.crashEvents.loading = true;
  },
  [LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_RESPONSE] : ({assetDetailHistoryGrid: {history}}: TrackingState, {data}) => {
    history.crashEvents.loading = false;
    history.crashEvents.results = data;
  },
  [LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_ERROR] : ({assetDetailHistoryGrid: {history}}: TrackingState, {error}) => {
    history.crashEvents.error   = error;
    history.crashEvents.loading = false;
  },
  [SET_ASSET_HISTORY_GRID_COLUMNS] : ({assetDetailHistoryGrid: {history, grid}}: TrackingState, {columnState}) => {
    onSetGridColumns({
      isLoading  : history.loading,
      preference : ASSET_DETAIL_HISTORY_PREFERENCE,
      grid
    }, columnState);
  },
  [UPDATE_ASSET_HISTORY_GRID_COLUMNS] : ({assetDetailHistoryGrid: {grid}}: TrackingState, {columnState}) => {
    onUpdateGridColumns({grid, preference: ASSET_DETAIL_HISTORY_PREFERENCE}, columnState);
  },
  [TOGGLE_ASSET_HISTORY_GRID_EXPANDED_GROUPS] : (
    {assetDetailHistoryGrid: {history: {expandedGroups}}}: TrackingState, {id, isExpanded}
  ) => {
    if (!isExpanded) {
      const index = expandedGroups.indexOf(id);
      expandedGroups.splice(index, 1);
    } else {
      expandedGroups.push(id);
    }
  },
  [SET_HOVERED_ASSET_DETAIL_HISTORY_EVENT] : ({assetDetailHistoryGrid: {history}}: TrackingState, {event}) => {
    history.hoveredEvent = event;
  },
  [SET_TRACKING_MAP_IS_ASSET_DETAIL_HISTORY] : ({maps}: TrackingState, {flag}) => {
    maps.isAssetDetailHistory = flag;
  },
  [SET_LOADER_INDICATOR_STATUS] : ({assets}: TrackingState, {value}) => {
    assets.assetsLoading = value;
  },
  [SET_ASSETS_COUNT] : ({assets}: TrackingState, {islargeFleetMode, count}) => {
    assets.assetsCount = !islargeFleetMode ? count : 0;
  },
  [LOAD_SC_DEVICES_REQUEST] : ({supplyChainDevices}: TrackingState) => {
    supplyChainDevices.devices = [];
    supplyChainDevices.updates = [];
  },
  [LOAD_SC_DEVICES_RESPONSE] : ({supplyChainDevices}: TrackingState, {scDevices}) => {
    supplyChainDevices.devices  = scDevices;
    supplyChainDevices.updates  = [];
    supplyChainDevices.isLoaded = true;
  },
  [LOAD_SC_DEVICES_ERROR] : ({supplyChainDevices}: TrackingState, {error}) => {
    supplyChainDevices.devices  = [];
    supplyChainDevices.updates  = [];
    supplyChainDevices.error    = error;
    supplyChainDevices.isLoaded = true;
  },
  [LOAD_SC_DEVICES_UPDATES] : ({supplyChainDevices}: TrackingState, {scDevicesUpdates}) => {
    supplyChainDevices.updates = scDevicesUpdates;
  },
  [SET_SC_DEVICES_OPACITY] : ({supplyChainDevices}: TrackingState, {opacity}) => {
    supplyChainDevices.opacity = opacity;
  },
  [SET_TRACKING_GRID_FILTER_MODEL] : ({grid}: TrackingState, {model}) => {
    grid.filterModel = model;
  },
  [SET_TRACKING_GRID_STATE] : ({grid}: TrackingState, {state}) => {
    if (state.reset) {
      grid.state = state;
    } else {
      grid.state = {
        ...grid.state,
        ...state
      };
    }
  },
  [SET_FLEET_UPDATE_FLAG] : (draft: TrackingState, {state}) => {
    draft.v2.fleetUpdateFlag = state;
  },
  [SET_FLEET_UPDATE_RESET_FLAG] : (draft: TrackingState, {state}) => {
    draft.v2.fleetUpdateResetFlag = state;
  },
  [SET_FLEET_UPDATE_FETCHING] : (draft: TrackingState, {key, isFetching}) => {
    const keys = draft.v2.fleetUpdateFetchingKeys;
    if (isFetching) {
      if (!keys.includes(key)) {
        keys.push(key);
      }
    } else {
      draft.v2.fleetUpdateFetchingKeys = keys.filter(existingKey => existingKey !== key);
    }
  },
  [SET_LARGE_FLEET_GRID_COUNT] : ({grid}: TrackingState, {count}) => {
    grid.count = count;
  },
  [SET_MAP_VIEWS] : ({mapViews}: TrackingState, {mapViewsToSave}) => {
    mapViews.savedMapViews =  mapViewsToSave;
  },
  [SET_EDITABLE_DEVICE] : ({supplyChainDevices}: TrackingState, {device}) => {
    supplyChainDevices.deviceToEdit = device;
  },
  [SET_ASSET_SELECTION_TYPE] : ({assets}: TrackingState, {value}) => {
    assets.assetSelectionType = value;
  },
  [RESET_SC_DEVICES] : ({supplyChainDevices}: TrackingState) => {
    supplyChainDevices.updates = [];
    supplyChainDevices.error   = null;
  },
  [TOGGLE_DEVICE_LABELLING] : ({supplyChainDevices}, {status}) => {
    supplyChainDevices.isLabelActive = status;
  },
  [SET_DEVICE_LABEL_CONTENT] : ({supplyChainDevices}, {labelContent}) => {
    supplyChainDevices.labelContent = labelContent;
  },
  [SET_ROUTE_MATCHING_ENABLED] : ({history}: TrackingState, {state}) => {
    history.routeMatchingEnabled = state;
  },
  [SET_ASSETS_SORT_DESCENDING] : ({assets}: TrackingState, {order}) => {
    assets.isAssetsSortDescending = order;
  },
  [TRACKING_ASSETS_UPDATES] : ({assets}: TrackingState, {updates, isAutoRefresh}) => {
    assets.updates       = updates;
    assets.isAutoRefresh = isAutoRefresh;
  },
  [SET_BOTTOM_GRID_HEIGHT] : ({persistView}: TrackingState, {height}) => {
    persistView.bottomGridHeight = height;
  },
  [CLEAR_SELECTED_FILTERS] : ({filters}: TrackingState) => {
    filters.assetCategories.selected = [];
    filters.assetStatus.selected = [];
    filters.assetTypes.selected = [];
    filters.assets.selected = [];
    filters.devices.selected = [];
    filters.geoFences.selected = [];
    filters.groups.selected = [];
    filters.routes.selected = [];
    filters.boundary = [];
  },
  [APPLY_DATE] : (draft: TrackingState, {date}) => {
    draft.filters.date.startDate = date?.fromDate || startOfDay();
    draft.filters.date.endDate   = date?.toDate || endOfDay();
    draft.filters.date.period    = date?.selectedRadio || DateSelectorValues.Live;
  },
  [SET_SELECTED_FILTERS_ACTIVE] : (draft: TrackingState, {isActive}) => {
    draft.isSelectedFiltersActive = isActive;
  },
  [SET_TRACKING_MODE] : (draft: TrackingState, {trackingMode}) => {
    draft.trackingMode = trackingMode;
  },
  [SET_TIME_MODE] : (draft: TrackingState, {mode}) => {
    draft.timeMode            = mode;
    draft.isTimeModePanelOpen = true;
  },
  [SET_TIME_MODE_PANEL_OPEN] : (draft: TrackingState, {isOpen}) => {
    draft.isTimeModePanelOpen = isOpen;
  },
  [SET_TIMELINE_PRESET] : (draft: TrackingState, {preset}) => {
    draft.timelinePreset = preset;
  },
  [SET_TIMELINE_DURATION] : (draft: TrackingState, {duration}) => {
    draft.timelineDuration = duration;
  },
  [SET_HISTORY_PRESET] : (draft: TrackingState, {preset}) => {
    draft.historyPreset = preset;
  },
  [SET_HISTORY_FROM] : (draft: TrackingState, {from}) => {
    draft.historyFrom = from;
  },
  [SET_HISTORY_TO] : (draft: TrackingState, {to}) => {
    draft.historyTo = to;
  },
  [SET_HISTORY_ACTIVE] : (draft: TrackingState, {isActive}) => {
    draft.isHistoryActive = isActive;
  },
  [RESET_GRID_FORCE_UPDATE] : ({fleetMode: {largeFleetAssetList}}: TrackingState, {update = false}) => {
    largeFleetAssetList.updateGrid = update;
  },
  [SET_BOUNDARY_FILTER] : (draft: TrackingState, {boundary}) => {
    draft.filters.boundary = boundary;
  },
  [RESET_ASSETS_TRACKING] : (draft: TrackingState, {boundary}) => {
    draft.assets.assets               = [];
    draft.assets.updates              = [];
    draft.fleetMode.assetsAggregation = [];
  },
  [RESET_INITIALIZE_TRACKING] : (draft: TrackingState) => {
    draft.isInitialized = false;
  },
  [SET_LARGE_FLEET_MODE] : (draft: TrackingState, {isHFM}) => {
    draft.fleetMode.isLargeFleetMode = isHFM;
  },
  [SET_TRACKING_ASSET_COUNT_LOAD] : ({assets}: TrackingState, {state}) => {
    assets.countLoading = state;
  },
  [SET_TRACKING_ASSET_COUNT] : ({fleetMode}: TrackingState, {largeFleetMode, response}) => {
    fleetMode.largeFleetAssetCount = largeFleetMode ? response?.count : 0;
  },
  [SET_TRACKING_GRID_FILTERED_ASSETS] : (draft: TrackingState, {assets, devices}) => {
    draft.assets.filteredAssets = assets;
    draft.assets.filteredDevices = devices;
  },
  [SET_TRACKING_ASSET_SNAP_TO_ROAD] : (draft: TrackingState, {isSnapToRoadEnabled}) => {
    draft.isSnapToRoadEnabled = isSnapToRoadEnabled;
  },
  [SET_FM_ASSET_INITIALIZED] : (draft: TrackingState, {isFMAssetInitialized}) => {
    draft.isFMAssetInitialized = isFMAssetInitialized;
  },
  [SET_GEOFENCE_LOADED] : (draft: TrackingState, {isGeofenceLoaded}) => {
    draft.isGeofenceLoaded = isGeofenceLoaded;
  },
  [SET_MAP_WIDGET_SELECTED] : (draft: TrackingState, {selected}) => {
    draft.mapWidgetSelected = selected;
    if (selected === ToolbarEnum.LiveHistory) {
      draft.historyV2.persistLiveHistory = true;
    }
  },
  [SET_PERSIST_LIVE_HISTORY] : (draft: TrackingState, {status}) => {
    draft.historyV2.persistLiveHistory = status;
  },
  [SET_HISTORY_RINGS] : (draft: TrackingState, {rings}) => {
    draft.historyV2.rings = rings;
  },
  [RUN_LIVE_HISTORY] : (draft: TrackingState, {rings}) => {
    const displayReport           = draft.historyV2.displayReport;
    draft.historyV2.isActive      = true;
    draft.historyV2.displayReport = !displayReport;
    if (rings) {
      draft.historyV2.rings = rings;
    }
  },
  [CANCEL_LIVE_HISTORY] : (draft: TrackingState) => {
    draft.historyV2.isActive = false;
    draft.historyV2.isIOProfileFetching = false;
    draft.historyV2.persistLiveHistory = false;
    draft.historyV2.assetsSearchActive = false;
    draft.historyV2.assetSearchList = [];
    draft.historyV2.initialAssets = [];
    draft.historyV2.selectedAsset = null;
    draft.historyV2.rings = null;
  },
  [CLEAR_HISTORY_SEARCH_ASSET] : (draft: TrackingState) => {
    draft.historyV2.assetsSearchActive = false;
    draft.historyV2.assetSearchList = [];
    draft.historyV2.selectedAsset = null;
  },
  [SET_LIVE_HISTORY_ASSET_SEARCH_ACTIVE] : (draft: TrackingState, {active}) => {
    draft.historyV2.assetsSearchActive = active;
    if (!active) {
      draft.historyV2.selectedAsset = null;
    }
  },
  [SET_LIVE_HISTORY_REPORT_TYPE] : (draft: TrackingState, {reportType}) => {
    draft.historyV2.reportType = reportType;
  },
  [SET_LIVE_HISTORY_SEARCH_TIME] : (draft: TrackingState, {time}) => {
    draft.historyV2.intervalInHours = time;
  },
  [SET_LIVE_HISTORY_INPUT_EVENTS] : (draft: TrackingState, {inputEvents}) => {
    draft.historyV2.inputEvents = inputEvents;
  },
  [SET_LIVE_HISTORY_SELECTED_ASSET] : (draft: TrackingState, {selectedAsset}) => {
    draft.historyV2.selectedAsset = selectedAsset;
  },
  [SET_LIVE_HISTORY_ASSET_SEARCH_LIST] : (draft: TrackingState, {asset, isBulk}) => {
    if (isBulk) {
      draft.historyV2.assetSearchList = asset;
    } else {
      const list = draft.historyV2.assetSearchList || [];
      list.unshift(asset);
      draft.historyV2.assetSearchList = list;
    }
  },
  [CLEAR_LIVE_HISTORY_ASSET_SEARCH_LIST] : (draft: TrackingState, {selectedIds}) => {
    let assetList = [];
    const selectedAsset   = draft.historyV2.selectedAsset;
    const assetSearchList = draft.historyV2.assetSearchList || [];
    const shouldClearList = !selectedIds?.length || assetSearchList.length === 1;
    if (!shouldClearList) {
      assetList = assetSearchList.filter(item => !selectedIds.includes(item.assetId));
    }
    const selectedAssetexists = assetList?.find(({assetId}) => assetId === selectedAsset?.assetId);
    if (!assetList.length || !selectedAssetexists) {
      draft.historyV2.selectedAsset = null;
    }
    draft.historyV2.assetSearchList = assetList;
  },
  [SET_LIVE_HISTORY_ERROR] : (draft: TrackingState, {showError, assetCount}) => {
    draft.historyV2.error.show = showError;
    draft.historyV2.error.assetCount = assetCount;
  },
  [SET_LIVE_HISTORY_INITIAL_ASSETS] : (draft: TrackingState, {assets}) => {
    draft.historyV2.initialAssets = assets;
  },
  [SET_LIVE_HISTORY_ASSET_FETCHING] : (draft: TrackingState, {isFetching}) => {
    draft.historyV2.isFetching = isFetching;
  },
  [SET_LIVE_HISTORY_ASSET_UI_FETCHING] : (draft: TrackingState, {isUIFetching}) => {
    draft.historyV2.isUIFetching = isUIFetching;
  },
  [SET_LIVE_HISTORY_IO_PROFILE_FETCHING] : (draft: TrackingState, {isIOProfileFetching}) => {
    draft.historyV2.isIOProfileFetching = isIOProfileFetching;
  },
  [SET_LIVE_HISTORY_HIDE_NON_SELECTED_ASSETS] : (draft: TrackingState, {status}) => {
    draft.historyV2.hideNonSelectedAssetsActive = status;
  },
  [SET_FLEET_LAYER_PERSISTENCE_ACTIVE] : (draft: TrackingState, {status}) => {
    draft.maps.isFleetLayerPersistenceActive = status;
  },
  [SET_CONTEXT_MENU_LOCATION] : (draft: TrackingState, {location, visibility}) => {
    draft.contextMenu.location = location;
    draft.contextMenu.visibility = visibility;
  },
  [SET_CONTEXT_MENU_VISIBILITY] : (draft: TrackingState, {visibility}) => {
    draft.contextMenu.visibility = visibility;
  },
  [SET_BOUNDARY_FILTER_MODE] : (draft: TrackingState, {mode}) => {
    draft.boundaryMode = mode;
  },
  [SET_BOUNDARY_PANEL_OPEN] : (draft, {mode}) => {
    if (mode) {
      draft.boundaryMode = mode;
    }
  },
  [SET_TRACKING_ASSET_UPDATES_PLAYBACK_STATE] : (draft, {state}) => {
    draft.isAssetUpdatesEnabled = state;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: TrackingState = initialState, action) => {
  reducers?.[action?.type]?.(draft, action);
  return draft;
});
